import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../../ui';
import { api } from '../api';
import { createActivo } from '../hooks/useActivos';
import SearchableSelect from './SearchableSelect';
import LocalidadSelect from './LocalidadSelect';
import { camposDeUbicacion } from '../utils/ubicacion';
import { avisoUnaPorPersona, CAMPO_CONFIRMACION } from '../utils/unaPorPersona';

/**
 * ActivoForm — alta de un activo, DINÁMICO según el fields_schema del
 * tipo. Cada tipo pide datos distintos:
 *
 *   string            → input text
 *   text              → textarea
 *   number / decimal  → input number
 *   date              → input date  (devuelve 'YYYY-MM-DD', que es justo
 *                                    lo que valida el backend)
 *   select            → select con field.options
 *   *_link            → select poblado con GET /options?link=&scope=
 *                        (user→personas, company→empresas, office→oficinas…)
 *
 * La REFERENCIA no se pide: la genera el backend (B2) al crear, igual
 * para tipos con y sin stock. El estado tampoco se pide (queda
 * 'disponible' por defecto; cambiarlo es una acción posterior).
 *
 * props:
 *   tipo       el tipo resuelto (con .slug, .scope, .fields_schema)
 *   scope      'interno' | 'colab'
 *   onCancel   () => void
 *   onCreated  (activoCreado) => void
 */

// tipo de campo *_link → valor del parámetro `link` del endpoint P1
const LINK_OF = {
  user_link: 'user',
  company_link: 'company',
  empresa_link: 'company',
  office_link: 'office',
  business_link: 'business',
};

const INPUT_CLS =
  'w-full border border-slate-200 rounded-md px-3 py-2 text-sm ' +
  'focus:outline-none focus:ring-2 focus:ring-blue-200';

export default function ActivoForm({ tipo, scope, onCancel, onCreated }) {
  const schema = Array.isArray(tipo?.fields_schema) ? tipo.fields_schema : [];
  const ubic = camposDeUbicacion(schema);

  const [values, setValues] = useState({});
  const [opts, setOpts] = useState({}); // { fieldName: [{id,nombre}] }
  const [loadingOpts, setLoadingOpts] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);
  // Una por persona (01/11): si la persona ya tiene uno del tipo y el modo es «avisar», se pregunta
  // aquí y «Crear igualmente» reenvía con la confirmación. Cambiar cualquier campo la quita.
  const [avisoUPP, setAvisoUPP] = useState(null);
  useEffect(() => { setAvisoUPP(null); }, [values]);

  // Cargar opciones de los campos de vínculo
  useEffect(() => {
    let cancel = false;
    const links = schema.filter((f) => LINK_OF[f.type]);
    if (links.length === 0) {
      setOpts({});
      return undefined;
    }
    setLoadingOpts(true);
    Promise.all(
      links.map((f) =>
        api
          .get('/options', { link: LINK_OF[f.type], scope })
          .then((r) => ({ name: f.name, items: r?.data || r || [] }))
          .catch(() => ({ name: f.name, items: [] }))
      )
    ).then((res) => {
      if (cancel) return;
      const map = {};
      res.forEach((x) => {
        map[x.name] = x.items;
      });
      setOpts(map);
      setLoadingOpts(false);
    });
    return () => {
      cancel = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo?.slug, scope]);

  const set = (name, v) => setValues((s) => ({ ...s, [name]: v }));

  const errors = useMemo(() => {
    const e = [];
    schema.forEach((f) => {
      if (f.required) {
        const v = values[f.name];
        if (v === undefined || v === null || v === '') {
          e.push(`"${f.label || f.name}" es obligatorio`);
        }
      }
    });
    return e;
  }, [schema, values]);

  const canSubmit = errors.length === 0 && !submitting && !loadingOpts;

  async function handleSubmit(confirmarUPP = false) {
    setServerError(null);
    setSubmitting(true);
    try {
      // Payload: solo campos con valor; *_link y number/decimal → Number
      const payload = {};
      schema.forEach((f) => {
        let v = values[f.name];
        if (v === undefined || v === null || v === '') return;
        if (LINK_OF[f.type] || f.type === 'number' || f.type === 'decimal') {
          v = Number(v);
        }
        payload[f.name] = v;
      });
      // Coste fijo (no entra en fields_schema). Solo si el tipo lo lleva.
      // Vacío → null (NULL en BBDD); valor → Number. La validación
      // (>= 0, numérico) la hace el backend (normalizeCoste).
      if (tipo.tiene_coste) {
        const c = values.coste;
        payload.coste = (c === '' || c == null) ? null : Number(c);
      }
      if (confirmarUPP) payload[CAMPO_CONFIRMACION] = true;
      const created = await createActivo({ slug: tipo.slug, scope, payload });
      if (onCreated) onCreated(created);
    } catch (err) {
      const aviso = avisoUnaPorPersona(err);
      if (aviso && aviso.confirmable) { setAvisoUPP(aviso.pregunta); return; }
      setServerError(
        err?.response?.data?.error ||
          err?.message ||
          'Error al crear el activo'
      );
    } finally {
      setSubmitting(false);
    }
  }

  function renderField(f) {
    const common = {
      value: values[f.name] ?? '',
      onChange: (e) => set(f.name, e.target.value),
      className: INPUT_CLS,
    };

    if (LINK_OF[f.type]) {
      const items = opts[f.name] || [];
      const select = (
        <SearchableSelect
          value={values[f.name] ?? ''}
          onChange={(v) => set(f.name, v)}
          options={items}
          loading={loadingOpts}
          placeholder="— Seleccionar —"
        />
      );
      if (f.type !== 'user_link') return select;
      // Desde el 15/10 el servidor convierte esto en una ASIGNACIÓN con su
      // evento (assetService.create). Antes el activo nacía «disponible» con
      // la persona puesta y sin evento, y la baja de esa persona no lo veía.
      return (
        <div>
          {select}
          <p className="mt-1 text-xs text-slate-500">
            Si eliges persona, el activo queda asignado a ella desde hoy, con su evento de asignación.
          </p>
          {/* LA RESERVA PARA QUIEN VA A ENTRAR NO SE HACE AQUÍ. Poner aquí a alguien que aún no ha
              entrado lo asigna con la fecha de hoy, que es la de preparación y no la de entrega.
              La reserva vive en el material de su alta, que asigna al marcarlo entregado (15/10). */}
          <p className="mt-1 text-xs text-amber-700">
            ¿Es para alguien que va a incorporarse? No pongas aquí la persona: dalo de alta sin ella y
            elígelo en el material de su alta. Se asignará el día que se marque entregado.
          </p>
        </div>
      );
    }
    if (ubic && f.name === ubic.localidad.name) {
      // Localidad del catálogo del INE de la provincia elegida (Fase 3b).
      return (
        <LocalidadSelect
          provincia={values[ubic.provincia.name] || ''}
          value={values[f.name] ?? ''}
          onChange={(v) => set(f.name, v)}
        />
      );
    }
    if (f.type === 'select') {
      const items = (f.options || []).map((o) => ({ id: o, nombre: o }));
      const esProvincia = ubic && f.name === ubic.provincia.name;
      return (
        <SearchableSelect
          value={values[f.name] ?? ''}
          onChange={(v) => {
            // Otra provincia: la localidad de la anterior ya no vale.
            if (esProvincia && v !== (values[f.name] ?? '')) set(ubic.localidad.name, '');
            set(f.name, v);
          }}
          options={items}
          placeholder="— Seleccionar —"
        />
      );
    }
    if (f.type === 'text') {
      return <textarea {...common} rows={3} />;
    }
    if (f.type === 'date') {
      return <input type="date" {...common} />;
    }
    if (f.type === 'number' || f.type === 'decimal') {
      return (
        <input
          type="number"
          step={f.type === 'decimal' ? 'any' : '1'}
          {...common}
        />
      );
    }
    return <input type="text" {...common} />;
  }

  if (!tipo) return null;

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">
        La <span className="font-medium text-slate-700">referencia</span> se
        genera automáticamente al crear el activo.
      </p>

      {loadingOpts && (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Cargando opciones…
        </div>
      )}

      <div className="space-y-3">
        {schema.map((f) => (
          <div key={f.name}>
            <label className="text-slate-500 text-xs mb-1 block">
              {f.label || f.name}
              {f.required && <span className="text-red-500"> *</span>}
            </label>
            {renderField(f)}
          </div>
        ))}
        {schema.length === 0 && (
          <p className="text-sm text-slate-500">
            Este tipo no tiene campos definidos.
          </p>
        )}

        {/* Coste — columna fija del tipo (fuera del bucle del schema).
            Solo aparece cuando tipo.tiene_coste === true. Vacío = NULL. */}
        {tipo.tiene_coste && (
          <div>
            <label className="text-slate-500 text-xs mb-1 block">Coste</label>
            <div className="relative">
              <input
                type="number"
                step="0.01"
                min="0"
                value={values.coste ?? ''}
                onChange={(e) => set('coste', e.target.value)}
                className={INPUT_CLS + ' pr-8'}
                placeholder="0,00"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                €
              </span>
            </div>
          </div>
        )}
      </div>

      {avisoUPP && (
        <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{avisoUPP}</span>
        </div>
      )}
      {serverError && (
        <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{serverError}</span>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-2">
        <Button variant="secondary" onClick={onCancel} disabled={submitting}>
          Cancelar
        </Button>
        <Button onClick={() => handleSubmit(!!avisoUPP)} disabled={!canSubmit}>
          {submitting ? (
            <span className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Creando…
            </span>
          ) : (
            avisoUPP ? 'Crear igualmente' : 'Crear activo'
          )}
        </Button>
      </div>
    </div>
  );
}
